import { css } from '@emotion/react'
import { format } from 'date-fns'
import { observer } from 'mobx-react'
import { generatePath, useNavigate } from 'react-router-dom'

import { tJosa } from '~/@app/i18n'
import { routeName } from '~/@common/constants'
import { textEllipsis, typo } from '~/@common/styles'

import type { LectureGroup, MiddleChapterLecture } from '../@service/Lecture.service'

const formatVideoLength = (totalSeconds: number): string =>
  `${String(Math.floor(totalSeconds / 60)).padStart(2, '0')}:${String(totalSeconds % 60).padStart(2, '0')}`

type Props = {
  lectureGroups: LectureGroup[]
}

const LectureTable = ({ lectureGroups }: Props) => {
  const navigate = useNavigate()

  const handleClickLecture = (lectureId: MiddleChapterLecture['id']) => {
    navigate(
      generatePath(routeName.student.lectureDetail, {
        lectureId: String(lectureId),
      }),
    )
  }

  return (
    <div css={S.table}>
      <div css={S.tableHeader}>
        <div css={S.colThumbnail} />
        <div css={S.colChapter}>단원명</div>
        <div css={S.colTitle}>{tJosa('강의')}명</div>
        <div css={S.colFixed}>최근 재생일</div>
        <div css={S.colFixed}>영상 시간</div>
        <div css={S.colAction}>학습하기</div>
      </div>

      {lectureGroups.map((group) => (
        <div key={group.leadingCurriculum.id}>
          <div css={S.groupHeader}>{group.leadingCurriculum.name}</div>

          {group.middleChapterLectures.map((lecture) => (
            <div css={S.groupItem} key={lecture.id}>
              <div css={S.colThumbnail}>
                <img
                  src={lecture.thumbnailUrl}
                  alt={`${tJosa('강의')} 썸네일`}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div css={[S.colChapter, S.chapterCell]}>
                <span>{lecture.middleChapterName}</span>
              </div>
              <div css={S.colTitle}>{lecture.title}</div>
              <div css={S.colFixed}>
                {lecture.latestPlayedAt
                  ? format(new Date(lecture.latestPlayedAt), 'yy.MM.dd')
                  : '-'}
              </div>
              <div css={S.colFixed}>
                {lecture.videoLengthSeconds != null
                  ? formatVideoLength(lecture.videoLengthSeconds)
                  : '-'}
              </div>
              <div css={S.colAction}>
                <button css={S.button} onClick={() => handleClickLecture(lecture.id)}>
                  학습하기
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

export default observer(LectureTable)

const S = {
  table: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
    border-radius: var(--Radius-300);
  `,
  tableHeader: css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 24px;
    background: var(--Colors-Gray-100);
    font-weight: 700;
    ${typo.body02};
    color: var(--Colors-Gray-600);
  `,
  groupHeader: css`
    display: flex;
    align-items: center;
    padding: 6px 24px;
    background: var(--Colors-Gray-200);
    border-top: 1px solid var(--Colors-Gray-300);
    ${typo.caption01};
    font-weight: 700;
    color: var(--Colors-Gray-900);
  `,
  groupItem: css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 24px;
    border-top: 1px solid var(--Colors-Gray-300);
    background: var(--White);
    ${typo.body02};
    color: var(--Colors-Gray-900);
  `,
  colThumbnail: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 59px;

    img {
      width: 100%;
      height: 30px;
      object-fit: cover;
      border-radius: var(--Radius-100);
      background: var(--Colors-Gray-200);
    }
  `,
  colChapter: css`
    width: 180px;
  `,
  colTitle: css`
    flex: 1;
    padding: 0 20px;
    ${textEllipsis(1)}
  `,
  colFixed: css`
    width: 100px;
    text-align: center;
  `,
  colAction: css`
    width: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  chapterCell: css`
    display: flex;
    align-items: center;
    gap: 10px;
  `,

  button: css`
    display: flex;
    width: 100px;
    height: 36px;
    padding: 0 16px;
    align-items: center;
    justify-content: center;
    border-radius: var(--Radius-100);
    border: 1px solid var(--Colors-Gray-400);
    background: var(--Colors-Gray-100);
  `,
}
